<script setup>
import {ref, computed, watch} from 'vue';
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from 'primevue/button';
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import { useServerTable } from "@/composables/useServerTable.js";
import { useWorksStore } from '@/stores/work.js';
import { RouterLink } from 'vue-router';
import WorkForm from "@/components/work/WorkForm.vue";
import Tag from "primevue/tag";

const props = defineProps({
  customerId: { type: [Number, String], required: false },
  facilityId: { type: [Number, String], required: false },
  contractId: { type: [Number, String], required: false },
  contractNumber: { type: String, required: false, default: '' },
  status: { type: String, default: 'ACTIVE' },
});

const store = useWorksStore();

const {
  data: works,
  totalRecords,
  loading,
  rowsPerPage,
  onPageChange,
  setSearch,
  setExtra,
  firstIndex,
  load,
} = useServerTable({
  initialRows: 5,
  debounceMs: 500,
  loadPage: params => store.loadWorksList({ ...params, page_size: params.rows }),
});

watch(
    () => [props.customerId, props.facilityId, props.contractId, props.status],
    ([customer, facility, contract, status]) => {
      setExtra({ customer, facility, contract, status });
    },
    { immediate: true }
);


// Локальное состояние открытия формы
const showWorkForm = ref(false);

function openAddWork() {
  if (!props.contractId) return; // защита: форма создания требует contractId
  showWorkForm.value = true;
}


const showSearchControls = computed(() => {
  if (loading.value) return true;
  const total = Number(totalRecords.value) || 0;
  const pageSize = Number(rowsPerPage.value) || 0;
  if (!pageSize) return total > 0;
  return total >= pageSize;
});
</script>

<template>
  <div class="flex flex-col items-start gap-0">
    <div class="flex justify-between items-center w-full">
      <span class="font-semibold">Работы:</span>
      <div  class="flex items-center gap-2">
        <IconField v-if="showSearchControls">
          <InputIcon class="pi pi-search" />
          <InputText placeholder="Поиск работ..." @update:modelValue="setSearch" class="h-7" size="small" />
        </IconField>
        <Button
            icon="pi pi-plus"
            rounded
            text
            size="small"
            :disabled="!contractId"
            :title="!contractId ? 'Нет ID договора' : 'Добавить работу'"
            @click="openAddWork"
        />

      </div>
    </div>
    <DataTable
        :value="works"
        :paginator="totalRecords > rowsPerPage"
        :rows="rowsPerPage"
        :totalRecords="totalRecords"
        :loading="loading"
        @page="onPageChange"
        :first="firstIndex()"
        lazy
        class="w-full text-sm"
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
        currentPageReportTemplate="от {first} до {last} из {totalRecords}"
        tableStyle="min-width: 100%"
        emptyMessage="Работ нет"
    >
      <Column style="width: 100%">
        <template #body="{ data }">
          <div class="flex justify-between items-center">
            <RouterLink :to="`/works/${data.id}`" class="hover:underline truncate">
              <i class="pi pi-clipboard text-xs text-gray-500 mr-2 align-middle"></i>
              {{ data.name || data.item_name }}
            </RouterLink>
            <Tag
                v-if="data.workflow_state_name"
                severity="info"
                :value="data.workflow_state_name"
                rounded
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
  <WorkForm
      :visible="showWorkForm"
      :contract-id="Number(contractId)"
      :contract-title="contractNumber"
      @update:visible="v => showWorkForm = v"
      @saved="load"
  />


</template>

<style scoped>
:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  border: none !important;
  background: transparent !important;
  border-bottom: none !important;
}
:deep(.p-datatable-tbody > tr > td) {
  padding: 0 !important;
  padding-left: 16px !important;
}
:deep(.p-datatable-thead > tr > th) {
  padding: 2px !important;

}
:deep(.p-paginator .p-paginator-prev),
:deep(.p-paginator .p-paginator-next) {
  height: 2rem;
  min-width: 2rem;
}
:deep(.p-tag-label) {
  font-weight: normal;
  font-size: 0.875rem;
}
</style>